import { useContext, useState } from 'react';

import AdminModal from '../../reusable/admin-modal/admin-modal.component';
import Spinner from '../../reusable/spinner/spinner.component';

import { ToastContext } from '../../../contexts/toast.context';
import Client from '../../../tools/client';

import {
  FieldGroup,
  Label,
  Input,
  Note,
  Button,
} from '../../configuration/sections/configuration-sections.styles';

const client = new Client();

const IntegrationSecretDisplay = ({ integration, publicKey, onSecretChange }) => {
  const [form, setForm] = useState({});
  const [loadingKey, setLoadingKey] = useState(null);
  const [deletingKey, setDeletingKey] = useState(null);
  const [modalKeyName, setModalKeyName] = useState(null);

  const { errorToast, successToast } = useContext(ToastContext);

  const keySchemaMap = integration.keySchemas.reduce((acc, schema) => {
    acc[schema.keyName] = schema;
    return acc;
  }, {});

  const handleChange = (keyName, value) => {
    setForm(prev => ({ ...prev, [keyName]: value }));
  };

  const handleCreate = async (keyName) => {
    const value = form[keyName];
    const schema = keySchemaMap[keyName];
    if (!value || !schema) return;

    try {
      setLoadingKey(keyName);

      let payloadValue = value;
      let encryptionMethod = 'plain';

      if (schema.encrypted && !schema.public) {
        if (!publicKey) throw new Error('Missing public key for encryption');
        const { encryptWithPublicKey } = await import('../../../tools/encryption/encryptionSecret');
        const encrypted = await encryptWithPublicKey(value, publicKey);
        payloadValue = {
          encryptedValue: encrypted,
          iv: '',
        };
        encryptionMethod = 'rsa-public';
      }

      const createRes = await client.createIntegrationSecret({
        integrationId: integration.id,
        keyName,
        value: payloadValue,
        encryptionMethod,
        type: schema.type || 'secret',
      });

      const updatedSecrets = [...integration.secrets, createRes];

      requestAnimationFrame(() => {
        onSecretChange?.(integration.id, updatedSecrets);
      });

      successToast(`${keyName} saved successfully`);

      setForm(prev => {
        const updated = { ...prev };
        delete updated[keyName];
        return updated;
      });
    } catch (err) {
      console.error(`Failed to create secret for key: ${keyName}`, err);
      errorToast(`Error saving ${keyName}: ${err.message}`);
    } finally {
      setLoadingKey(null);
    }
  };

  const confirmDelete = async () => {
    const keyName = modalKeyName;
    if (!keyName) return;

    try {
      const secret = integration.secrets.find(s => s.keyName === keyName);
      if (!secret?.id) throw new Error('Missing secret ID for deletion');

      setDeletingKey(keyName);
      setModalKeyName(null);

      await client.deleteIntegrationSecretById(secret.id);

      const updatedSecrets = integration.secrets.filter(s => s.keyName !== keyName);

      requestAnimationFrame(() => {
        onSecretChange?.(integration.id, updatedSecrets);
      });

      successToast(`${keyName} deleted successfully`);
    } catch (err) {
      console.error(`Failed to delete secret for key: ${keyName}`, err);
      errorToast(`Error deleting ${keyName}: ${err.message}`);
    } finally {
      setDeletingKey(null);
    }
  };

  return (
    <>
      {integration.keySchemas.map(schema => {
        const existing = integration.secrets.find(s => s.keyName === schema.keyName);
        const value = form[schema.keyName] || '';
        const isLoading = loadingKey === schema.keyName || deletingKey === schema.keyName;
        const canSave = !existing && value && !isLoading;

        return (
          <FieldGroup key={schema.keyName} style={{ position: 'relative' }}>
            <Label htmlFor={schema.keyName}>{schema.label}</Label>

            {existing ? (
              <>
                <Input
                  type="text"
                  value={schema.public ? existing.value : '••••••••'}
                  disabled
                />
                <Note>This key already exists. Delete it before replacing.</Note>
              </>
            ) : (
              <Input
                type={schema.public ? 'text' : 'password'}
                id={schema.keyName}
                value={value}
                onChange={(e) => handleChange(schema.keyName, e.target.value)}
                placeholder={schema.description || ''}
              />
            )}

            <div style={{ marginTop: '0.5rem' }}>
              <Button
                primary={!existing && !!value && !isLoading}
                disabled={isLoading || (!existing && !value)}
                onClick={() =>
                  existing
                    ? setModalKeyName(schema.keyName)
                    : handleCreate(schema.keyName)
                }
              >
                {isLoading ? (
                  <Spinner size="small" />
                ) : existing ? (
                  'Delete'
                ) : (
                  'Save'
                )}
              </Button>
            </div>
          </FieldGroup>
        );
      })}

      <AdminModal
        show={!!modalKeyName}
        setShow={() => setModalKeyName(null)}
        title="Delete Secret Key?"
        message={`Are you sure you want to delete the "${modalKeyName}" key? This cannot be undone.`}
        action={confirmDelete}
        actionText="Confirm Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export default IntegrationSecretDisplay;